import { createContext, useContext, useMemo, type ReactNode } from 'react'
import useToggle from 'hooks/useToggle'

/** Shared open-state for cruise search filter inputs (desktop dropdown vs mobile drawer). */
export interface CruiseSearchInputContextValue {
  /** True when either desktop dropdown or mobile drawer is open */
  isOpen: boolean
  /** Desktop dropdown sheet open state */
  isDesktopOpen: boolean
  open: () => void
  close: () => void

  /** Mobile / tablet drawer open state */
  isMobileOpen: boolean
  openMobile: () => void
  closeMobile: () => void
  toggleMobile: () => void
}

const CruiseSearchInputContext = createContext<CruiseSearchInputContextValue | null>(null)

export interface CruiseSearchInputProviderProps {
  children: ReactNode
  /** Sets both surfaces initial open state (e.g. isolated list tests). */
  defaultPanelOpen?: boolean
  /** Desktop dropdown; ignored when defaultPanelOpen is set. */
  defaultOpen?: boolean
  /** Mobile drawer; ignored when defaultPanelOpen is set. */
  defaultMobileOpen?: boolean
}

export function CruiseSearchInputProvider({
  children,
  defaultPanelOpen,
  defaultOpen = false,
  defaultMobileOpen = false,
}: CruiseSearchInputProviderProps) {
  const desktopInitial = defaultPanelOpen !== undefined ? defaultPanelOpen : defaultOpen
  const mobileInitial = defaultPanelOpen !== undefined ? defaultPanelOpen : defaultMobileOpen

  const {
    value: isDesktopOpen,
    on: open,
    off: close,
  } = useToggle(desktopInitial)
  const {
    value: isMobileOpen,
    on: openMobile,
    off: closeMobile,
    toggle: toggleMobile,
  } = useToggle(mobileInitial)

  const value = useMemo(
    (): CruiseSearchInputContextValue => ({
      isOpen: isMobileOpen || isDesktopOpen,
      isDesktopOpen,
      open,
      close,
      isMobileOpen,
      openMobile,
      closeMobile,
      toggleMobile,
    }),
    [isDesktopOpen, open, close, isMobileOpen, openMobile, closeMobile, toggleMobile],
  )

  return (
    <CruiseSearchInputContext.Provider value={value}>
      {children}
    </CruiseSearchInputContext.Provider>
  )
}

export function useCruiseSearchInput(): CruiseSearchInputContextValue {
  const ctx = useContext(CruiseSearchInputContext)
  if (ctx == null) {
    throw new Error('useCruiseSearchInput must be used within CruiseSearchInputProvider')
  }
  return ctx
}
